'use client';

import { c as _c } from "react/compiler-runtime";
import { Skeleton } from '@/comps/primitives/skeleton';
import { AiProficiencyReportCard } from '@/domains/pages/ai-proficiency/components/ai-proficiency-report-card';
import { SegmentScaleChartSkeleton } from '@/domains/pages/ai-proficiency/components/segment-scale-chart';
import { AIFI_LEVEL_CHART_COLUMN_SPAN, AIFI_LEVEL_GRID_TEMPLATE_COLUMNS, AIFI_LEVELS } from '@/domains/pages/ai-proficiency/constants';
import { cn } from '@/utils/shad';
function ScoreCardSkeleton() {
  const $ = _c(4);
  let t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton className="h-11 w-44" />;
    t1 = <Skeleton className="mt-3 h-4 w-full max-w-xs" />;
    t2 = <div aria-hidden={true} className="mt-2 h-4" />;
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t0 = $[0];
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex flex-col">{t0}{t1}{t2}<div className="mt-6"><Skeleton className="h-3 w-20" /><Skeleton className="mt-2 h-3.5 w-full" /><Skeleton className="mt-2.5 h-3 w-48 max-w-full" /></div></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
function OverTimeChartSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex min-h-0 flex-1 flex-col"><div className="mb-4 flex flex-wrap items-center gap-4"><Skeleton className="h-3 w-28" /></div><div className="min-h-55 flex-1" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function LadderColumnSkeleton(t0) {
  const $ = _c(15);
  const {
    showBar: t1,
    isFirst: t2,
    isLast: t3
  } = t0;
  const showBar = t1 === undefined ? true : t1;
  const isFirst = t2 === undefined ? false : t2;
  const isLast = t3 === undefined ? false : t3;
  let t4;
  let t5;
  if ($[0] !== isFirst || $[1] !== isLast) {
    const gutter = cn(isFirst ? "pl-8" : "pl-6", isLast ? "pr-8" : "pr-6");
    t5 = "flex min-w-0 flex-1 flex-col border-r border-lightborder last:border-r-0";
    t4 = cn("flex min-w-0 grow flex-col pt-6 pb-4", gutter);
    $[0] = isFirst;
    $[1] = isLast;
    $[2] = t4;
    $[3] = t5;
  } else {
    t4 = $[2];
    t5 = $[3];
  }
  let t6;
  let t7;
  let t8;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="flex items-center gap-3"><Skeleton className="size-14 rounded-full" /><Skeleton className="h-6 w-16" /></div>;
    t7 = <Skeleton className="mt-2 h-3 w-full" />;
    t8 = <div aria-hidden={true} className="mt-1.5 h-3" />;
    $[4] = t6;
    $[5] = t7;
    $[6] = t8;
  } else {
    t6 = $[4];
    t7 = $[5];
    t8 = $[6];
  }
  let t9;
  if ($[7] !== showBar) {
    t9 = showBar ? <Skeleton className="mt-4 h-3.5 w-full" /> : null;
    $[7] = showBar;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== t4 || $[10] !== t9) {
    t10 = <div className={t4}>{t6}{t7}{t8}{t9}</div>;
    $[9] = t4;
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== t10 || $[13] !== t5) {
    t11 = <div className={t5}>{t10}</div>;
    $[12] = t10;
    $[13] = t5;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  return t11;
}
function LadderSkeleton(t0) {
  const $ = _c(4);
  const {
    showBar: t1
  } = t0;
  const showBar = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== showBar) {
    t2 = AIFI_LEVELS.map((level, index) => <LadderColumnSkeleton key={level} showBar={showBar} isFirst={index === 0} isLast={index === AIFI_LEVELS.length - 1} />);
    $[0] = showBar;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t2) {
    t3 = <div className="flex divide-x divide-lightborder">{t2}</div>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
function LevelMixChartSkeleton() {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      gridTemplateColumns: AIFI_LEVEL_GRID_TEMPLATE_COLUMNS
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex min-w-0 flex-col border-r border-lightborder">{Array.from({
        length: 3
      }).map(_temp)}</div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="grid items-stretch" style={t0}>{t1}<div className="flex min-w-0 flex-col gap-3 py-4 pr-8 pl-6" style={{
        gridColumn: `span ${AIFI_LEVEL_CHART_COLUMN_SPAN}`
      }}><Skeleton className="h-3 w-72 max-w-full" /><div className="relative mt-1 h-55 w-full" /></div></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function _temp(_, index) {
  return <div key={index} className="flex min-w-0 flex-1 flex-col justify-center border-b border-b-lightborder py-4 pr-6 pl-8 last:border-b-0"><Skeleton className="h-8 w-16" /><div aria-hidden={true} className="mt-2.5 h-4" /></div>;
}
function SegmentPickerSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex flex-wrap gap-2 border-b border-b-lightborder px-8 py-4"><Skeleton className="h-6 w-80 max-w-full rounded-full" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function AiProficiencyChartsLoadingSkeleton(t0) {
  const $ = _c(7);
  const {
    showSegmentLadderBar: t1
  } = t0;
  const showSegmentLadderBar = t1 === undefined ? false : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="col-span-1 flex border-r border-r-lightborder"><AiProficiencyReportCard title="AI Proficiency score" className="flex min-h-0 flex-1 flex-col [&>section]:min-h-0 [&>section]:flex-1" contentClassName="flex min-h-0 flex-1 flex-col justify-center !py-3" headerTrailing={<Skeleton className="h-7 w-32 rounded-full" />}><ScoreCardSkeleton /></AiProficiencyReportCard></div>;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="grid grid-cols-3 items-stretch border-b border-b-lightborder">{t2}<div className="col-span-2 flex"><AiProficiencyReportCard title="AI Proficiency over time" className="flex min-h-0 flex-1 flex-col [&>section]:flex-1" contentClassName="flex min-h-0 flex-1 flex-col"><OverTimeChartSkeleton /></AiProficiencyReportCard></div></div>;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== showSegmentLadderBar) {
    t4 = <div className="grid grid-cols-4 items-start border-b border-b-lightborder"><div className="col-span-4"><AiProficiencyReportCard title="AI Proficiency groups" contentClassName="p-0"><LadderSkeleton showBar={showSegmentLadderBar} /></AiProficiencyReportCard></div></div>;
    $[2] = showSegmentLadderBar;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="grid grid-cols-4 items-start"><div className="col-span-4"><AiProficiencyReportCard title="AI Proficiency groups progress over time" contentClassName="p-0"><LevelMixChartSkeleton /></AiProficiencyReportCard></div></div>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t4) {
    t6 = <>{t3}{t4}{t5}</>;
    $[5] = t4;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  return t6;
}
export function AiProficiencyPageLoadingSkeleton(t0) {
  const $ = _c(10);
  const {
    showSegmentPicker: t1,
    segmentScaleTitle
  } = t0;
  const showSegmentPicker = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== showSegmentPicker) {
    t2 = showSegmentPicker ? <SegmentPickerSkeleton /> : null;
    $[0] = showSegmentPicker;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== segmentScaleTitle) {
    t3 = segmentScaleTitle ? <AiProficiencyReportCard title={segmentScaleTitle} className="border-b border-b-lightborder"><SegmentScaleChartSkeleton /></AiProficiencyReportCard> : null;
    $[2] = segmentScaleTitle;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== showSegmentPicker) {
    t4 = <AiProficiencyChartsLoadingSkeleton showSegmentLadderBar={showSegmentPicker} />;
    $[4] = showSegmentPicker;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t2 || $[7] !== t3 || $[8] !== t4) {
    t5 = <div className="border-b border-b-lightborder" data-testid="nebuly-page-loading">{t2}{t3}{t4}</div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}